// src/Components/AppShell.jsx
// Wraps the tab destinations (Home, Settings) and the pages opened from them
// (Find a public game, My Rooms) with a shared header
// and responsive nav. The old hamburger + HeaderMenu dropdown is retired —
// this is what replaces it: a minimal header (logo, How to Play, coins) plus
// the same 3 destinations shown inline on desktop or as a bottom bar on
// narrow viewports (see BottomTabBar).
import React, { useState } from "react";
import { Outlet, NavLink, Link } from "react-router-dom";
import { HelpCircle, Home as HomeIcon, Settings as SettingsIcon } from "lucide-react";
import CoinBalance from "./CoinBalance";
import CoinStore from "./CoinStore";
import GameRulesModal from "./Modals/GameRulesModal";
import BottomTabBar from "./BottomTabBar";

export const TABS = [
  // Rooms was a third tab; it only repeated Home's "Find a public game" link,
  // so it was folded into Home, matching the mobile app.
  { key: "home", to: "/", label: "Home", icon: HomeIcon },
  { key: "settings", to: "/account", label: "Settings", icon: SettingsIcon },
];

const AppShell = () => {
  const [showGameRules, setShowGameRules] = useState(false);
  const [showCoinStore, setShowCoinStore] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50">
      <header className="fixed top-0 left-0 right-0 bg-gradient-to-br from-orange-50 to-red-50 border-b border-red-200 shadow-sm z-40">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          {/* Logo, not a heading — each page underneath supplies its own
              single <h1>. Two H1s on one page (this plus a page's own)
              confuses search engines about what the page is actually about. */}
          <span className="lg:hidden text-xl font-bold text-brand-accent">NameIt!</span>
          <Link to="/" className="hidden lg:inline text-xl font-bold text-brand-accent" aria-label="NameIt! home">
            NameIt!
          </Link>

          {/* Mid-width nav: the destinations inline in the header. From lg up the
              logo goes home and Settings is an icon beside the coins. */}
          <nav className="hidden sm:flex lg:hidden items-center gap-1">
            {TABS.map(({ key, to, label, icon: Icon }) => (
              <NavLink
                key={key}
                to={to}
                end={to === "/"}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    isActive
                      ? "bg-brand-accentMuted text-brand-accent"
                      : "text-gray-500 hover:bg-white/50"
                  }`
                }
              >
                <Icon size={16} />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowGameRules(true)}
              className="p-2 hover:bg-white/50 rounded-xl transition"
              aria-label="How to play"
            >
              <HelpCircle size={20} className="text-gray-600" />
            </button>
            <button
              onClick={() => setShowCoinStore(true)}
              className="rounded-lg"
              aria-label="Buy coins"
            >
              <CoinBalance size="small" showBuyButton={false} />
            </button>
            <NavLink
              to="/account"
              aria-label="Settings"
              title="Settings"
              className={({ isActive }) =>
                `hidden lg:flex p-2 rounded-xl transition ${
                  isActive ? "bg-brand-accentMuted text-brand-accent" : "text-gray-600 hover:bg-white/50"
                }`
              }
            >
              <SettingsIcon size={20} />
            </NavLink>
          </div>
        </div>
      </header>

      <div className="pt-16 pb-24 sm:pb-8">
        <Outlet />
      </div>

      {/* Narrow-viewport nav; the header nav above takes over at sm: */}
      <BottomTabBar tabs={TABS} />

      <GameRulesModal isOpen={showGameRules} onClose={() => setShowGameRules(false)} />
      <CoinStore isOpen={showCoinStore} onClose={() => setShowCoinStore(false)} />
    </div>
  );
};

export default AppShell;
